import { routesMap } from "./routesMap";
import { analytics } from "./GTM";
import Intercom, { shutdown, update, trackEvent } from "@intercom/messenger-js-sdk";
const { getUser } = require("./userHelpers");

export const initServices = () => {
  /**
   * Amplitude
   * https://www.docs.developers.amplitude.com/data/sdks/marketing-analytics-browser/
   *
   */
  analytics.init();
};

const APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;

/**
 * Boot Intercom for every signed-in user.
 *
 * Identity Verification: Intercom strongly recommends signing the user_id with
 * a server-generated HMAC-SHA256 of the Intercom secret key. The signed hash
 * is passed as `user_hash`. Without this, any client can impersonate another
 * user via DevTools. The backend must expose the hash on the session / login
 * response (see REACT_APP_INTERCOM_USER_HASH fallback for bootstrapping only).
 *
 * Docs:
 *   https://www.intercom.com/help/en/articles/183-enable-identity-verification-for-your-web-users
 *   https://developers.intercom.com/installing-intercom/web/installation
 */
export const initIntercom = (userData = {}) => {
  if (!APP_ID) return;
  try {
    const user = getUser();
    if (!user) return;

    const email = user.email || userData.email;
    const userId = user.user_id || user.localId || userData.user_id;
    if (!email || !userId) return;

    const name =
      user.first_name || user.firstName || user.displayName || userData.name;
    const companyName =
      user.company_name || user.companyName || userData.companyName;
    const emailVerified = user.email_verified || user.emailVerified;
    const rawCreatedAt = user.created_at || userData.created_at;
    const createdAt = rawCreatedAt
      ? (rawCreatedAt > 1e12 ? Math.floor(rawCreatedAt / 1000) : rawCreatedAt)
      : undefined;
    const userHash =
      user.intercom_user_hash || userData.intercom_user_hash ||
      process.env.REACT_APP_INTERCOM_USER_HASH;

    Intercom({
      app_id: APP_ID,
      user_id: userId,
      name,
      email,
      created_at: createdAt,
      email_verified: emailVerified,
      ...(userHash ? { user_hash: userHash } : {}),
      ...(companyName ? { company: { name: companyName } } : {}),
      role: user.role,
      org_role: user.org_role,
      org_id: user.org_id,
    });
  } catch (error) {
    console.error("Error initializing Intercom:", error);
  }
};

/**
 * Nudge Intercom that the page / route changed so it re-fetches messages.
 * Safe to call on every navigation.
 */
export const updateIntercom = () => {
  if (!APP_ID) return;
  try {
    update();
  } catch (error) {
    console.error("Error updating Intercom:", error);
  }
};

/**
 * Push fresh user attributes to Intercom (plan / usage / company etc.).
 */
export const updateIntercomUserData = ({
  email,
  firstName,
  plan,
  audioUsed,
  audioTotal,
  audioWithMediaUsed,
  audioWithMediaTotal,
  companyName,
}) => {
  if (!APP_ID) return;
  try {
    const user = getUser();
    if (!user) return;

    const userId = user.user_id || user.localId;
    const emailVerified = user.email_verified || user.emailVerified;
    if (!email || !userId) return;

    const audioLeft = (audioTotal || 0) - (audioUsed || 0);
    const audioWithMediaLeft =
      (audioWithMediaTotal || 0) - (audioWithMediaUsed || 0);

    update({
      user_id: userId,
      email,
      name: firstName,
      "Subscribed Plan": plan || "",
      plan: plan || "",
      "First Name": firstName || "",
      "Company name": companyName || "",
      "Audio Usage Left from Plan": audioLeft + audioWithMediaLeft,
      email_verified: emailVerified,
    });
  } catch (error) {
    console.error("Error updating Intercom user data:", error);
  }
};

/**
 * Clear Intercom session. MUST be called on logout — otherwise the next user
 * signing in on the same browser will inherit the previous user's conversation.
 * https://developers.intercom.com/installing-intercom/web/methods (shutdown)
 */
export const shutdownIntercom = () => {
  if (!APP_ID) return;
  try {
    shutdown();
  } catch (error) {
    console.error("Error shutting down Intercom:", error);
  }
};

/**
 * Record a custom event against the current Intercom user. Used for triggering
 * Intercom workflows / in-app messages (e.g. "dispute_raised", "first_login").
 */
export const trackIntercomEvent = (name, metadata) => {
  if (!APP_ID || !name) return;
  try {
    trackEvent(name, metadata);
  } catch (error) {
    console.error("Error tracking Intercom event:", error);
  }
};

/**
 *
 * @param {*string} pathname
 * @param {*string} search
 * @returns string
 *
 * This function is used to get the page name for analytics
 *
 */
const pathName = (pathname, search) => {
  let path = pathname.split("/")[1];
  if (path === "auth") {
    path = pathname.split("/")[2];
  }
  const routeName = routesMap[path] || "page view";
  if (typeof routeName === "object") {
    const routeSubName = routeName[search];
    return routeSubName;
  }
  return routeName;
};

/**
 * @param {*string} path
 * @param {*string} search
 * This function is used to track the page view
 * and send the page name to analytics
 *
 */
export const analyticsUpdate = (path, search) => {
  const analyticsPageName = pathName(path, search);
  analytics.track(analyticsPageName, {
    "content type": "page view",
    url: path + search,
  });
};
